import { uniqBy } from "es-toolkit/compat";

import type { VariableType } from "@skilitics/nautilus-core";

import type { GetParentContextById } from "../../state/index.ts";
import type { DesignerNode, GetManifestByTypeId, ReferenceableVariable } from "../../types/index.ts";
import { subtypeSatisfies, type ValueSubtypeRequirement } from "../../values/valueSubtypes.ts";
import type { ResolveActiveNodes } from "./resolveActiveNodes.ts";

export type ReferenceableVariableWithId = Readonly<ReferenceableVariable<any> & { nodeId: number; id: string }>;

export const getNodeReferenceableVariablesCreate =
  (getManifest: GetManifestByTypeId, getParentContextById: GetParentContextById) =>
  (node: DesignerNode): ReadonlyArray<ReferenceableVariableWithId> => {
    const {
      designer: { getReferenceableVariables },
    } = getManifest(node.typeId);
    if (!getReferenceableVariables) {
      return [];
    }
    const variables = getReferenceableVariables(node, { parentContext: getParentContextById(node.id) }).map(
      (variable) => ({
        nodeId: node.id,
        id: `${node.id}:${variable.name}`,
        ...variable,
      }),
    );

    // verify they are in config
    // TODO: in future allow a default value as an alternative. needs to be accessible from state reducer to replace references on delete
    const configVariables = node.config.variables ?? {};
    const missingInConfig = variables.filter((v) => !(v.name in configVariables)).map((v) => v.name);
    if (missingInConfig.length) {
      throw new Error(
        `Reference variables must be defined in design-time config. on ${
          node.typeId
        } the following are not: ${missingInConfig.join(",")}`,
      );
    }
    // arguable prod only as it's kind of fundamental
    if (uniqBy(variables, "name").length !== variables.length) {
      // could specify which but so obvious to fix
      throw new Error(
        `Variable names not unique on 'getReferenceableVariables()' on Node Designer of type ${node.typeId}`,
      );
    }
    return variables;
  };

export const resolveReferenceableVariablesCreate =
  (resolveActiveNodes: ResolveActiveNodes, getNodeReferenceableVariables: GetNodeReferenceableVariables) =>
  (
    nodeId: number,
    type?: VariableType,
    /** Narrows within the type, on the same semantics as a value binding. */
    subtype?: ValueSubtypeRequirement,
  ): ReadonlyArray<ReferenceableVariableWithId> => {
    return resolveActiveNodes(nodeId)
      .flatMap(getNodeReferenceableVariables)
      .filter((v) => (type ? v.type === type : true) && subtypeSatisfies(subtype, v.subtype));
  };

export type GetNodeReferenceableVariables = ReturnType<typeof getNodeReferenceableVariablesCreate>;

export type ResolveReferenceableVariables = ReturnType<typeof resolveReferenceableVariablesCreate>;
